export function calculateBarWidth(score: number, maxScore = 5): string {
  const percentage = Math.min(Math.max((score / maxScore) * 100, 0), 100);
  return `${percentage}%`;
}

function isCJKDominant(text: string): boolean {
  const cjkChars = text.match(/[\u4e00-\u9fff\u3400-\u4dbf\uf900-\ufaff]/g);
  return (cjkChars?.length || 0) > text.length * 0.3;
}

export function truncateText(text: string, maxLength = 150): string {
  const effectiveMax = isCJKDominant(text) ? Math.floor(maxLength * 0.5) : maxLength;
  if (text.length <= effectiveMax) return text;
  return `${text.slice(0, effectiveMax).trimEnd()}...`;
}

export function getInitials(name: string): string {
  const words = name
    .trim()
    .split(/\s+/)
    .filter((word) => /[a-zA-Z]/.test(word));

  if (words.length === 0) return "";
  const first = words[0].match(/[a-zA-Z]/)?.[0]?.toUpperCase() || "";
  if (words.length === 1) return first;
  const last = words[words.length - 1].match(/[a-zA-Z]/)?.[0]?.toUpperCase() || "";
  return first + last;
}

const NATIONALITY_TO_COUNTRY_CODE: Record<string, string> = {
  american: "US",
  british: "GB",
  english: "GB",
  scottish: "GB",
  welsh: "GB",
  irish: "IE",
  canadian: "CA",
  australian: "AU",
  german: "DE",
  french: "FR",
  spanish: "ES",
  italian: "IT",
  dutch: "NL",
  portuguese: "PT",
  brazilian: "BR",
  mexican: "MX",
  indian: "IN",
  chinese: "CN",
  china: "CN",
  japanese: "JP",
  korean: "KR",
  singaporean: "SG",
  malaysian: "MY",
  thai: "TH",
  vietnamese: "VN",
  filipino: "PH",
  indonesian: "ID",
  pakistani: "PK",
  bangladeshi: "BD",
  sri_lankan: "LK",
  nepalese: "NP",
  nigerian: "NG",
  ghanaian: "GH",
  kenyan: "KE",
  south_african: "ZA",
  egyptian: "EG",
  saudi: "SA",
  emirati: "AE",
  turkish: "TR",
  russian: "RU",
  polish: "PL",
  swedish: "SE",
  norwegian: "NO",
  danish: "DK",
  finnish: "FI",
  greek: "GR",
  swiss: "CH",
  austrian: "AT",
  belgian: "BE",
  czech: "CZ",
  hungarian: "HU",
  romanian: "RO",
  colombian: "CO",
  argentinian: "AR",
  chilean: "CL",
  peruvian: "PE",
  new_zealander: "NZ",
  "new zealander": "NZ",
};

export function getNationalityCountryCode(nationality: string): string {
  if (!nationality) return "";
  const normalized = nationality
    .split(",")[0]
    .toLowerCase()
    .trim()
    .replace(/[-_\s]+/g, "_");
  return (NATIONALITY_TO_COUNTRY_CODE[normalized] || "").toLowerCase();
}

export function getNationalityFlagEmoji(nationality: string): string {
  if (!nationality) return "";
  const normalized = nationality
    .toLowerCase()
    .trim()
    .replace(/[-_\s]+/g, "_");
  const countryCode = NATIONALITY_TO_COUNTRY_CODE[normalized];
  if (!countryCode) return "";
  const codePoints = countryCode
    .toUpperCase()
    .split("")
    .map((char) => 0x1f1e6 + char.charCodeAt(0) - 65);
  return String.fromCodePoint(...codePoints);
}
